/**
 * ProjectRunBanner — a small always-visible banner shown while a swarm runs
 * inside a project. It pins the project's name + standing instructions so the
 * user (and reviewers) can see the grounding context the swarm is honouring.
 *
 * Props: projectId, theme (T: {bg,panel,border,textH,textB,textDim,accent})
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { FolderKanban, BookOpen, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectRunBanner({ projectId, theme: T }) {
  const [project, setProject] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/projects/${projectId}`)
      .then(({ data }) => { if (live) setProject(data); })
      .catch(() => {});
    return () => { live = false; };
  }, [projectId]);

  if (!project) return null;
  const instr = (project.instructions || "").trim();

  return (
    <div data-testid="project-run-banner"
      style={{ position: "sticky", top: 0, zIndex: 30, background: `${T.accent}12`,
        borderBottom: `1px solid ${T.accent}44`, backdropFilter: "blur(8px)",
        padding: "10px 20px", display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <FolderKanban size={15} color={T.accent} />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.textH }}>Running in project:</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.accent }} data-testid="project-run-banner-name">{project.name}</span>
        {instr && (
          <button onClick={() => setOpen((v) => !v)} data-testid="project-run-banner-toggle"
            style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 11.5, fontWeight: 600 }}>
            <BookOpen size={13} /> Instructions {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        )}
      </div>
      {instr && open && (
        <div data-testid="project-run-banner-instructions"
          style={{ fontSize: 12.5, lineHeight: 1.5, color: T.textB, whiteSpace: "pre-wrap",
            maxHeight: 120, overflowY: "auto", paddingLeft: 25 }}>
          {instr}
        </div>
      )}
    </div>
  );
}
